import { useEffect, useMemo, useState } from 'react';
import { FaLightbulb, FaClock } from 'react-icons/fa';
import AnswerButton from '../components/AnswerButton.jsx';
import HintModal from '../components/HintModal.jsx';
import { HINT_COIN_COST, QUESTION_TIME_SEC } from '../ads/adConfig.js';
import { useAds } from '../context/AdContext.jsx';
import { useCoins } from '../hooks/useCoins.jsx';

// Renders the current question and reports the chosen option upward.
// The parent (PlayPage) owns coins, ad triggers, and the reward modal.
export default function QuizScreen({ index, questions, locked, categoryTitle, onAnswer }) {
  const question = questions[index];
  const { showRewarded } = useAds();
  const { coins, addCoins } = useCoins();
  const [picked, setPicked] = useState(null);
  const [hintIdx, setHintIdx] = useState(null); // eliminated wrong option
  const [hintOpen, setHintOpen] = useState(false);
  const [hintBusy, setHintBusy] = useState(false);
  const [timeLeft, setTimeLeft] = useState(QUESTION_TIME_SEC);

  // Reset per-question state whenever the question changes.
  const key = question?.id;
  useMemo(() => {
    setPicked(null);
    setHintIdx(null);
    setHintOpen(false);
    setTimeLeft(QUESTION_TIME_SEC);
  }, [key]);

  // Countdown — paused while an answer is locked in, an ad is loading, or the
  // hint dialog is open.
  const paused = picked !== null || locked || hintOpen;
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setTimeLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [paused, key]);

  // Time's up → reveal the answer and count it as a miss.
  useEffect(() => {
    if (timeLeft === 0 && picked === null) {
      setPicked(-1); // sentinel: timed out (reveals correct, blocks input)
      onAnswer(-1, false);
    }
  }, [timeLeft, picked, onAnswer]);

  const handlePick = (i) => {
    if (picked !== null || locked || i === hintIdx) return;
    setPicked(i);
    onAnswer(i, i === question.answerIndex);
  };

  // Eliminate one wrong option (50:50).
  const applyHint = () => {
    const wrong = question.options
      .map((_, i) => i)
      .filter((i) => i !== question.answerIndex);
    setHintIdx(wrong[0]);
    setHintOpen(false);
  };

  const hintViaAd = async () => {
    setHintBusy(true);
    const { viewed } = await showRewarded();
    if (viewed) applyHint();
    setHintBusy(false);
  };

  const hintViaCoins = () => {
    if (coins < HINT_COIN_COST) return;
    addCoins(-HINT_COIN_COST);
    applyHint();
  };

  const stateFor = (i) => {
    if (i === hintIdx) return 'muted';
    if (picked === null) return 'idle';
    if (i === question.answerIndex) return 'correct';
    if (i === picked) return 'wrong';
    return 'muted';
  };

  if (!question) return null; // safety: nothing to render without a question

  const canHint = picked === null && hintIdx === null && !locked;
  const timePct = (timeLeft / QUESTION_TIME_SEC) * 100;
  const low = timeLeft <= 5;

  return (
    <div className="w-full max-w-lg mx-auto flex flex-col items-center">
      {categoryTitle && (
        <p className="text-xs font-black uppercase tracking-[0.3em] text-brand mb-3">
          {categoryTitle}
        </p>
      )}

      <div className="dark-stage w-full p-6 sm:p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden">
        {/* Countdown bar */}
        <div
          className="absolute top-0 left-0 h-1.5 transition-[width] duration-1000 ease-linear"
          style={{ width: `${timePct}%`, backgroundColor: low ? '#ef4444' : '#fbbf24' }}
        />

        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-violet-300">
            Question {index + 1} / {questions.length}
          </p>
          <div className="flex items-center gap-2">
            <span
              className={`flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] font-black tabular-nums ${
                low ? 'bg-red-500/20 text-red-300 animate-pulse' : 'bg-white/10 text-amber-300'
              }`}
            >
              <FaClock /> {timeLeft}s
            </span>
            <button
              onClick={() => setHintOpen(true)}
              disabled={!canHint}
              className="flex items-center gap-1.5 rounded-full bg-amber-400/15 text-amber-300 px-3 py-1.5 text-[10px] font-black uppercase tracking-wide hover:bg-amber-400/25 transition-colors disabled:opacity-40"
            >
              <FaLightbulb /> Hint
            </button>
          </div>
        </div>

        <h1 className="text-2xl sm:text-3xl font-black text-white leading-tight mb-8">
          {question.prompt}
        </h1>

        <div className="space-y-3 sm:space-y-4">
          {question.options.map((opt, i) => (
            <AnswerButton
              key={opt}
              label={opt}
              state={stateFor(i)}
              disabled={picked !== null || locked || i === hintIdx}
              onClick={() => handlePick(i)}
            />
          ))}
        </div>

        {picked === -1 && (
          <p className="mt-4 text-center text-xs font-bold text-red-300">⏱ Time’s up!</p>
        )}
      </div>

      {hintOpen && (
        <HintModal
          coins={coins}
          cost={HINT_COIN_COST}
          busy={hintBusy}
          onWatchAd={hintViaAd}
          onUseCoins={hintViaCoins}
          onClose={() => setHintOpen(false)}
        />
      )}
    </div>
  );
}
